import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Popconfirm, Space, Switch, Table } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { CheckCircleTwoTone, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import WarehouseCarrierModal from './WarehouseCarrierModal';
import { WarehouseCarrier } from '../../../../custom_types/wms-page';
import NoData from '../../../../shared/components/NoData';
import { fetchCarriersHandler } from '../../../../redux/carriersSlice';
import {
  deleteWarehouseCarrierHandler,
  fetchWarehouseCarriersHandler,
  selectWarehouseCarriers,
  selectWarehouseCarriersLoading,
  setShowWarehouseCarrierModal,
  updateWarehouseCarrierHandler
} from '../../../../redux/warehouseCarrierSlice';
import { getCarrierLogo } from '../../../../shared/utils/logo.helper';

interface WarehouseCarrierPanelProps {
  warehouseId: string;
}

const WarehouseCarrierPanel = ({ warehouseId }: WarehouseCarrierPanelProps) => {
  const dispatch = useDispatch();
  const warehouseCarriers = useSelector(selectWarehouseCarriers);
  const loading = useSelector(selectWarehouseCarriersLoading);
  const [curCarrier, setCurCarrier] = useState<WarehouseCarrier | undefined>(undefined);

  useEffect(() => {
    dispatch(fetchCarriersHandler);
    dispatch(fetchWarehouseCarriersHandler(warehouseId));
  }, [dispatch, warehouseId]);

  const addCarrierClickedHandler = () => {
    setCurCarrier(undefined);
    dispatch(setShowWarehouseCarrierModal(true));
  };

  const editCarrierClickedHandler = (record: WarehouseCarrier) => {
    setCurCarrier(record);
    dispatch(setShowWarehouseCarrierModal(true));
  };

  const statusChangeHandler = async (active: boolean, value: WarehouseCarrier) => {
    const data = { ...value, active };
    dispatch(updateWarehouseCarrierHandler(data));
  };

  const columns: any[] = [
    {
      title: '',
      key: 'logo',
      render: (text: string, record: WarehouseCarrier) => {
        const logo = getCarrierLogo(record.carrier);
        return <img style={{ width: '60px', height: '60px' }} src={logo} alt={record.carrier} />;
      }
    },
    {
      title: '物流公司',
      dataIndex: 'carrier',
      key: 'carrier'
    },
    {
      title: '账号名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '账号说明',
      dataIndex: 'description',
      key: 'description',
      render: (descriptions: string[]): ReactElement => {
        return (
          <ul>
            {descriptions.map((desc) => {
              return <li key={desc}>{desc}</li>;
            })}
          </ul>
        );
      }
    },
    {
      title: '物流服务',
      dataIndex: 'servies',
      key: 'services',
      render: (text: string, record: WarehouseCarrier): string => {
        return record.services.map((ele) => ele.key).join(', ');
      }
    },
    {
      title: '服务范围',
      dataIndex: 'regions',
      key: 'regions',
      render: (text: string, record: WarehouseCarrier): string => {
        return record.regions.join(', ');
      }
    },
    {
      title: '默认使用',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault: boolean) => {
        return isDefault ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : null;
      }
    },
    {
      title: '三方价格',
      dataIndex: 'thirdpartyPrice',
      key: 'thirdpartyPrice',
      render: (thirdpartyPrice: boolean) => {
        return thirdpartyPrice ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : null;
      }
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      render: (value: boolean, record: WarehouseCarrier) => (
        <Switch
          checkedChildren="启用"
          unCheckedChildren="停用"
          defaultChecked
          checked={value}
          onChange={() => statusChangeHandler(!value, record)}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (text: string, record: WarehouseCarrier) => (
        <Space size={0} split={<Divider type="vertical" />}>
          <Button size="small" type="link" onClick={() => editCarrierClickedHandler(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该账号?"
            placement="topRight"
            onConfirm={() =>
              dispatch(deleteWarehouseCarrierHandler(record.id, record.warehouseRef))
            }
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" type="link">
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <WarehouseCarrierModal warehouseId={warehouseId} warehouseCarrier={curCarrier} />
      <PageHeader
        title=""
        extra={[
          <Button
            icon={<SyncOutlined spin={loading} />}
            onClick={() => dispatch(fetchWarehouseCarriersHandler(warehouseId))}
          />,
          <Button key="1" type="primary" icon={<PlusOutlined />} onClick={addCarrierClickedHandler}>
            添加物流账号
          </Button>
        ]}
      />
      <Table<WarehouseCarrier>
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={warehouseCarriers}
        pagination={false}
        loading={loading}
        locale={{
          emptyText: <NoData />
        }}
        scroll={{ x: 1000 }}
      />
    </div>
  );
};

export default WarehouseCarrierPanel;
